'use client';
import { type ReactNode, useContext } from 'react';
import { clsx } from 'clsx';
import type { PropsWithClassName } from 'types/react-props';
import { AppContext } from '../../../context/AppContext';
import { SponsoredIconFull } from '../SponsoredIcon';
import { Title, type TitleProps } from '../Title';
import styles from './TeaserGridSection.module.scss';

export type TeaserGridSectionProps = PropsWithClassName<{
  readonly children: ReactNode;
  readonly title?: TitleProps['children'];
  readonly titleLink?: TitleProps['link'];
  readonly titleAs?: TitleProps['as'];
  readonly variant?:
    | 'default'
    | 'small'
    | 'small-aufmacher'
    | 'small-with-medium'
    | 'color'
    | 'rtl-plus'
    | 'rtl-plus-top5'
    | 'medium'
    | 'search';
  readonly isRtlPlus?: boolean;
  readonly isSponsored?: boolean;
  readonly anchor?: string;
}>;

const TeaserGridSection = ({
  children,
  title,
  titleLink,
  titleAs = 'h2',
  variant = 'default',
  isRtlPlus = false,
  className,
  isSponsored,
  anchor,
}: TeaserGridSectionProps) => {
  const {
    pageMetaData: { isStoryline },
  } = useContext(AppContext);

  return (
    <section className={clsx(styles.section, className)} id={anchor}>
      {!!title && (
        <Title link={titleLink} as={isStoryline ? 'div' : titleAs} isRtlPlus={isRtlPlus}>
          {title}
        </Title>
      )}
      {isSponsored && <SponsoredIconFull />}
      <div
        className={clsx(styles.grid, {
          [styles.default]: variant === 'default',
          [styles.smallAufmacher]: variant === 'small-aufmacher',
          [styles.smallWithMedium]: variant === 'small-with-medium',
          [styles.rtlPlus]: variant === 'rtl-plus',
          [styles.rtlPlusTop5]: variant === 'rtl-plus-top5',
          [styles.medium]: variant === 'medium',
          [styles.search]: variant === 'search',
        })}
      >
        {children}
      </div>
    </section>
  );
};

export default TeaserGridSection;
